import type {
  ConditionGroupMap,
  ConditionMap,
  RuleMap,
  RuleData,
  RuleType
} from "@certa/studio-core";
import { dbOps } from "../hooks/dbOps.store";
import type { HashMap } from "@certa/types";
import { getRulesMapFromMeta } from "../../../utils/rules";
import { updateComponent } from "./misc.actions";
import { ruleTypeData } from "../../../constants/rules.constants";
import type { RulesListViewComponentKeys } from "../../../forms/DBRulesListViewNew";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";
import { getRulesModel } from "../../../models/rules.model";

export const importRulesToStudio = (props: {
  processId: string;
  ruleMap: RuleMap;
  conditionGroupMap: ConditionGroupMap;
  conditionMap: ConditionMap;
  shouldSkipUpdatingModifiedAtTimestamp?: boolean;
}) => {
  const {
    processId,
    ruleMap,
    conditionGroupMap,
    conditionMap,
    shouldSkipUpdatingModifiedAtTimestamp = false
  } = props;

  dbOps().transaction(() => {
    dbOps().bulkSetRules(
      processId,
      Object.values(ruleMap),
      shouldSkipUpdatingModifiedAtTimestamp
    );
    dbOps().bulkSetConditionGroups(
      processId,
      Object.values(conditionGroupMap),
      shouldSkipUpdatingModifiedAtTimestamp
    );
    dbOps().bulkSetConditions(
      processId,
      Object.values(conditionMap),
      shouldSkipUpdatingModifiedAtTimestamp
    );
  });
};

/**
 * Replaces old field tag with new field tag that are referenced in rules
 */
export const changeFieldTagReferenceInRules = (props: {
  processId: string;
  ruleIds: string[];
  oldTag: string;
  newTag: string;
}) => {
  const { processId, ruleIds, oldTag, newTag } = props;
  const replaceOldTagWithNewTag = (obj: HashMap, keysToCheck?: string[]) => {
    let isModified = false;
    return {
      obj: JSON.parse(JSON.stringify(obj), (key, value) => {
        if (keysToCheck && !keysToCheck.includes(key)) return value;
        if (value === oldTag) {
          isModified = true;
          return newTag;
        }
        if (Array.isArray(value) && value.includes(oldTag)) {
          return value.map(val => {
            if (val === oldTag) {
              isModified = true;
              return newTag;
            }
            return val;
          });
        }
        return value;
      }),
      isModified
    };
  };

  ruleIds.forEach(ruleId => {
    const rule = dbOps().getRule(processId, ruleId);
    if (rule) {
      // Check rule actions
      if (rule?.actions) {
        const replaceResult = replaceOldTagWithNewTag(rule.actions);
        if (replaceResult.isModified) {
          dbOps().updateRule(processId, ruleId, { actions: replaceResult.obj });
        }
      }

      // Check target tag
      if (rule?.__targetFieldTag === oldTag) {
        dbOps().updateRule(processId, ruleId, { __targetFieldTag: newTag });
      }

      // Check top tevel condition group
      const checkTagInConditionGroup = (conditionGroupId: string) => {
        if (conditionGroupId) {
          const conditionGroupData = dbOps().getConditionGroup(
            processId,
            conditionGroupId
          );
          conditionGroupData?.groups?.forEach(conditionGroupId => {
            checkTagInConditionGroup(conditionGroupId);
          });
          conditionGroupData?.conditions?.forEach(conditionId => {
            const conditionData = dbOps().getCondition(processId, conditionId);
            if (conditionData) {
              const replaceResult = replaceOldTagWithNewTag(conditionData, [
                "firstCompareField",
                "secondCompareField"
              ]);
              if (replaceResult.isModified) {
                dbOps().updateCondition(
                  processId,
                  conditionId,
                  replaceResult.obj
                );
              }
            }
          });
        }
      };

      checkTagInConditionGroup(rule.topLevelGroupId);
    }
  });
};

const ruleMetaToDB = ({
  metaRuleMap,
  ruleType,
  processId
}: {
  processId: string;
  metaRuleMap: RuleMap;
  ruleType: RuleType;
}) => {
  const rulesForDB = getRulesMapFromMeta(metaRuleMap, ruleType);

  importRulesToStudio({
    processId,
    ruleMap: rulesForDB.ruleMap,
    conditionMap: rulesForDB.conditionMap,
    conditionGroupMap: rulesForDB.conditionGroupMap
  });

  return rulesForDB;
};

export const addRule = (props: {
  processId: string;
  componentType: RulesListViewComponentKeys;
  componentId: string;
  ruleType: RuleType;
  ruleId: string;
  ruleData: RuleData;
}) => {
  const { processId, componentType, componentId, ruleId, ruleType, ruleData } =
    props;

  if (ruleTypeData[ruleType].componentKey !== componentType) {
    throw new Error(`Invalid rule type for component type: ${componentType}`);
  }

  dbOps().transaction(
    () => {
      const component: unknown = dbOps().getComponent(
        processId,
        componentType,
        componentId
      );

      if (!component) {
        throw new Error(`Component not found for id: ${componentId}`);
      }

      const prevRuleIds: string[] =
        component[ruleTypeData[ruleType].ruleKey as keyof typeof component] ||
        [];

      ruleMetaToDB({
        processId,
        metaRuleMap: { [ruleId]: ruleData },
        ruleType
      });
      updateComponent({
        processId,
        componentType,
        componentId,
        componentProps: {
          [ruleTypeData[ruleType].ruleKey]: [...prevRuleIds, ruleId]
        }
      });
    },
    { intent: Intents.ADD_RULE }
  );
};

export const deleteRules = (props: {
  processId: string;
  componentType: RulesListViewComponentKeys;
  componentId: string;
  ruleType: RuleType;
  ruleIds: string[];
}) => {
  const { processId, componentType, componentId, ruleType, ruleIds } = props;

  if (ruleTypeData[ruleType].componentKey !== componentType) {
    throw new Error(`Invalid rule type for component type: ${componentType}`);
  }

  dbOps().transaction(
    () => {
      const component: unknown = dbOps().getComponent(
        processId,
        componentType,
        componentId
      );

      if (!component) {
        throw new Error(`Component not found for id: ${componentId}`);
      }

      const prevRuleIds: string[] =
        component[ruleTypeData[ruleType].ruleKey as keyof typeof component] ||
        [];

      const updatedRuleIds = prevRuleIds.filter(id => !ruleIds.includes(id));

      updateComponent({
        processId,
        componentType,
        componentId,
        componentProps: {
          [ruleTypeData[ruleType].ruleKey]: updatedRuleIds
        }
      });

      dbOps().removeRules(processId, ruleIds);
    },
    { intent: Intents.DELETE_RULES }
  );
};

export const updateRule = ({
  processId,
  ruleId,
  ruleData
}: {
  processId: string;
  ruleId: string;
  ruleData: Partial<RuleData>;
}) => {
  dbOps().transaction(
    () => {
      dbOps().updateRule(processId, ruleId, ruleData);
    },
    { intent: Intents.UPDATE_RULE }
  );
};

export const setRule = ({
  processId,
  ruleId,
  ruleData,
  ruleType
}: {
  processId: string;
  ruleId: string;
  ruleData: RuleData;
  ruleType: RuleType;
}) => {
  dbOps().transaction(
    () => {
      ruleMetaToDB({
        processId,
        metaRuleMap: { [ruleId]: ruleData },
        ruleType
      });
    },
    { intent: Intents.UPDATE_RULE }
  );
};

/**
 * Parses and store raw serialized rules to db
 */
export const ruleRawToDB = (
  processId: string,
  rawRules: Record<string, any>,
  ruleType: RuleType,
  targetFieldTag?: string
) => {
  const rulesForDB = getRulesModel(rawRules, ruleType, {
    processId,
    targetFieldTag
  });

  dbOps().transaction(
    () => {
      importRulesToStudio({
        processId,
        ruleMap: rulesForDB.ruleMap,
        conditionMap: rulesForDB.conditionMap,
        conditionGroupMap: rulesForDB.conditionGroupMap
      });
    },
    { intent: Intents.ADD_RULES }
  );

  return rulesForDB;
};
